import classNames from "classnames"

import CategoryBadge from "./CategoryBadge"
import { WIDE_ARTICLE_SRCSET_WIDTHS } from "./imageConfig"

import Link from "@components/elements/Link"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import LayoutItem from "@components/V2/Layouts/LayoutItem"
import { Color } from "@constants/V2/color"
import {
  getAccentColor,
  getBackgroundColorClass,
  getBorderColorClass,
  getTextColorClass,
} from "@utils/V2/color"
import React, { ElementType } from "react"

const FEATURED_CTA_TEXT = "Read now"
const PANEL_COLOR = Color.Charcoal
const PANEL_TEXT_CLASS = "md:text-white"

const getPanelColor = (cardColor: Color) =>
  cardColor === PANEL_COLOR ? Color.Coal : PANEL_COLOR

// Tailwind only detects literal class strings, so this cannot use a helper.
const getPanelBackgroundClass = (panelColor: Color) =>
  panelColor === Color.Coal ? "md:bg-coal" : "md:bg-charcoal"

type FeaturedArticleCardProps = {
  title: string
  imageUrl: string
  imageAlt: string
  category?: string
  readTimeText?: string
  summary?: string
  articleLink?: YocoCom.YocoLink
  backgroundColor: Color
  iconUrl?: string
  iconAlt: string
}

const FeaturedArticleCard = ({
  title,
  imageUrl,
  imageAlt,
  category,
  readTimeText,
  summary,
  articleLink,
  backgroundColor,
  iconUrl,
  iconAlt,
}: FeaturedArticleCardProps) => {
  const Wrapper = (articleLink ? Link : "div") as ElementType
  const panelColor = getPanelColor(backgroundColor)
  const textColor = getAccentColor(backgroundColor)
  const panelBackgroundClass = getPanelBackgroundClass(panelColor)

  return (
    <LayoutItem
      animateOnScroll
      className="col-span-full -mx-gutter-xs mb-24 block px-10 sm:-mx-gutter-sm md:-mx-gutter-md md:mb-[112px] md:mt-32 md:px-20 lg:-mx-gutter-lg xl:-mx-gutter-xl 4k:-mx-gutter-4k"
    >
      <Wrapper
        className={classNames(
          "group flex flex-col gap-10 rounded-16 border border-opacity-10 p-10 md:grid md:grid-cols-2 md:gap-32 md:rounded-20 md:border-0 md:p-80",
          getBorderColorClass(Color.Charcoal),
          getBackgroundColorClass(backgroundColor),
          { "cursor-pointer": !!articleLink }
        )}
        {...(articleLink ? { link: articleLink } : {})}
      >
        <div className="relative aspect-[4/3] overflow-hidden rounded-8 md:aspect-auto md:h-full md:min-w-0 md:rounded-10">
          <div className="absolute inset-0">
            <GatsbyStoryblokImage
              image={imageUrl}
              alt={imageAlt}
              aspectRatio="fullWidth"
              imageObjectFit="cover"
              sizes="(min-width: 900px) calc(50vw - 116px), calc(100vw - 42px)"
              srcSetWidths={WIDE_ARTICLE_SRCSET_WIDTHS}
              preserveAnimation
              className="h-full w-full"
            />
          </div>

          {category && (
            <CategoryBadge
              category={category}
              className="absolute bottom-10 left-10"
            />
          )}
        </div>

        <div
          className={classNames(
            "flex flex-col gap-10 rounded-8 px-10 py-8 md:aspect-square md:min-w-0 md:gap-0 md:rounded-10 md:px-48 md:py-40 md:ring-1 md:ring-inset md:ring-transparent md:transition-shadow md:duration-200 md:ease-out md:group-hover:ring-light-gray/20 md:group-focus-visible:ring-light-gray/20",
            panelBackgroundClass
          )}
        >
          {iconUrl && (
            <div
              className={classNames(
                "hidden h-[44px] w-[44px] flex-none items-center justify-center rounded-10 md:mb-48 md:flex",
                getBackgroundColorClass(backgroundColor)
              )}
            >
              <GatsbyStoryblokImage
                image={iconUrl}
                alt={iconAlt}
                aspectRatio="1:1"
                width={24}
                height={24}
              />
            </div>
          )}

          <div className="flex flex-col gap-8 md:block md:max-w-[485px]">
            <Typography
              className={classNames(
                "!text-body-md md:!text-lead-lg md:transition-colors md:duration-200 md:ease-out md:group-hover:text-blue md:group-focus-visible:text-blue",
                PANEL_TEXT_CLASS
              )}
              element="h2"
              size="lead-lg"
              font="grotesk"
              weight="medium"
              color={textColor}
              text={title}
            />

            {summary && (
              <Typography
                className={classNames("opacity-80 md:mt-16", PANEL_TEXT_CLASS)}
                size="body-md-dual"
                font="grotesk"
                weight="book"
                color={textColor}
                text={summary}
              />
            )}

            {readTimeText && (
              <Typography
                className={classNames("opacity-60 md:mt-24", PANEL_TEXT_CLASS)}
                size="body-sm"
                font="grotesk"
                weight="book"
                color={textColor}
                text={readTimeText}
              />
            )}
          </div>

          <div
            className={classNames(
              "mt-auto flex items-center gap-10 py-[5px] md:py-14",
              getTextColorClass(textColor),
              PANEL_TEXT_CLASS
            )}
          >
            <Typography
              className={PANEL_TEXT_CLASS}
              size="body-md-dual"
              font="grotesk"
              weight="medium"
              color={textColor}
              text={FEATURED_CTA_TEXT}
            />
            <span className="flex h-20 w-20 flex-none items-center justify-center md:h-[22px] md:w-[22px]">
              <ArrowIcon />
            </span>
          </div>
        </div>
      </Wrapper>
    </LayoutItem>
  )
}

export default FeaturedArticleCard
